import type { TMenuItem } from '@atlas/uicorn/lib/Control/Menu';

// The scores are arranged so that a continuous match of characters will
// result in a total score of 1.

// The best case, this character is a match, and either this is the start
// of the string, or the previous character was also a match.
const SCORE_CONTINUE_MATCH = 1;
// A new match at the start of a word scores better than a new match
// elsewhere as it's more likely that the user will type the starts
// of fragments.
// NOTE: We score word jumps between spaces slightly higher than slashes, brackets
// hyphens, etc.
const SCORE_SPACE_WORD_JUMP = 0.9;
const SCORE_NON_SPACE_WORD_JUMP = 0.8;
// Any other match isn't ideal, but we include it for completeness.
const SCORE_CHARACTER_JUMP = 0.17;
// If the user transposed two letters, it should be significantly penalized.
//
// i.e. "ouch" is more likely than "curtain" when "uc" is typed.
const SCORE_TRANSPOSITION = 0.1;
// The goodness of a match should decay slightly with each missing
// character.
//
// i.e. "bad" is more likely than "bard" when "bd" is typed.
//
// This will not change the order of suggestions based on SCORE_* until
// 100 characters are inserted between matches.
const PENALTY_SKIPPED = 0.999;
// The goodness of an exact-case match should be higher than a
// case-insensitive match by a small amount.
//
// i.e. "HTML" is more likely than "haml" when "HM" is typed.
//
// This will not change the order of suggestions based on SCORE_* until
// 1000 characters are inserted between matches.
const PENALTY_CASE_MISMATCH = 0.9999;
// Match higher for letters closer to the beginning of the word
// const PENALTY_DISTANCE_FROM_START = 0.9;

// If the word has more characters than the user typed, it should
// be penalised slightly.
//
// i.e. "html" is more likely than "html5" if I type "html".
//
// However, it may well be the case that there's a sensible secondary
// ordering (like alphabetical) that it makes sense to rely on when
// there are many prefix matches, so we don't make the penalty increase
// with the number of tokens.
const PENALTY_NOT_COMPLETE = 0.99;

const FIELD_POSITION_BONUS = 0.8;

// Minimum score threshold - matches below this will be filtered out
const MIN_SCORE_THRESHOLD = 0.1;

const IS_GAP_REGEXP = /[/_+.#"@[({&]/;
const COUNT_GAPS_REGEXP = /[/_+.#"@[({&]/g;
const IS_SPACE_REGEXP = /[\s-]/;
const COUNT_SPACE_REGEXP = /[\s-]/g;

function getScoreInner(
    string: string,
    abbreviation: string,
    lowerString: string,
    lowerAbbreviation: string,
    stringIndex: number,
    abbreviationIndex: number,
    memoizedResults: Record<string, number>,
) {
    if (abbreviationIndex === abbreviation.length) {
        if (stringIndex === string.length) {
            return SCORE_CONTINUE_MATCH;
        }
        return PENALTY_NOT_COMPLETE;
    }

    const memoizeKey = `${stringIndex},${abbreviationIndex}`;
    if (memoizedResults[memoizeKey] !== undefined) {
        return memoizedResults[memoizeKey];
    }

    const abbreviationChar = lowerAbbreviation.charAt(abbreviationIndex);
    let index = lowerString.indexOf(abbreviationChar, stringIndex);
    let highScore = 0;

    let score, transposedScore, wordBreaks, spaceBreaks;

    while (index >= 0) {
        score = getScoreInner(
            string,
            abbreviation,
            lowerString,
            lowerAbbreviation,
            index + 1,
            abbreviationIndex + 1,
            memoizedResults,
        );
        if (score > highScore) {
            if (index === stringIndex) {
                score *= SCORE_CONTINUE_MATCH;
            } else if (IS_GAP_REGEXP.test(string.charAt(index - 1))) {
                score *= SCORE_NON_SPACE_WORD_JUMP;
                wordBreaks = string.slice(stringIndex, index - 1).match(COUNT_GAPS_REGEXP);
                if (wordBreaks && stringIndex > 0) {
                    score *= Math.pow(PENALTY_SKIPPED, wordBreaks.length);
                }
            } else if (IS_SPACE_REGEXP.test(string.charAt(index - 1))) {
                score *= SCORE_SPACE_WORD_JUMP;
                spaceBreaks = string.slice(stringIndex, index - 1).match(COUNT_SPACE_REGEXP);
                if (spaceBreaks && stringIndex > 0) {
                    score *= Math.pow(PENALTY_SKIPPED, spaceBreaks.length);
                }
            } else {
                score *= SCORE_CHARACTER_JUMP;
                if (stringIndex > 0) {
                    score *= Math.pow(PENALTY_SKIPPED, index - stringIndex);
                }
            }

            if (string.charAt(index) !== abbreviation.charAt(abbreviationIndex)) {
                score *= PENALTY_CASE_MISMATCH;
            }
        }

        if (
            (score < SCORE_TRANSPOSITION &&
                lowerString.charAt(index - 1) === lowerAbbreviation.charAt(abbreviationIndex + 1)) ||
            (lowerAbbreviation.charAt(abbreviationIndex + 1) === lowerAbbreviation.charAt(abbreviationIndex) && // allow duplicate letters. Ref #7428
                lowerString.charAt(index - 1) !== lowerAbbreviation.charAt(abbreviationIndex))
        ) {
            transposedScore = getScoreInner(
                string,
                abbreviation,
                lowerString,
                lowerAbbreviation,
                index + 1,
                abbreviationIndex + 2,
                memoizedResults,
            );

            if (transposedScore * SCORE_TRANSPOSITION > score) {
                score = transposedScore * SCORE_TRANSPOSITION;
            }
        }

        if (score > highScore) {
            highScore = score;
        }

        index = lowerString.indexOf(abbreviationChar, index + 1);
    }

    memoizedResults[memoizeKey] = highScore;
    return highScore;
}

function formatInput(string: string) {
    // convert all valid space characters to space so they match each other
    return string.toLowerCase().replace(COUNT_SPACE_REGEXP, ' ');
}

const normalizeDiacritics = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

function getScore(string: string, abbreviation: string, aliases: string[]): number {
    /* NOTE:
     * in the original, we used to do the lower-casing on each recursive call, but this meant that toLowerCase()
     * was the dominating cost in the algorithm, passing both is a little ugly, but considerably faster.
     */
    string = aliases && aliases.length > 0 ? `${string + ' ' + aliases.join(' ')}` : string;
    return getScoreInner(string, abbreviation, formatInput(string), formatInput(abbreviation), 0, 0, {});
}

function getObjectScore<T = Record<string, unknown>>(value: T, query: string, fields?: (keyof T)[]): number {
    if (!fields || fields.length === 0) return 0;

    return fields.reduce((acc, field, index) => {
        const fieldValue = value[field];
        const keywords = (value as TMenuItem)?.keywords || [];

        if (typeof fieldValue !== 'string') {
            return acc;
        }

        const normalizedFieldValue = normalizeDiacritics(fieldValue);

        const score = getScore(normalizedFieldValue, query, keywords);
        if (score === 0) return acc;

        // Add bonus based on field index and order
        const bonus = score > FIELD_POSITION_BONUS ? Math.max(fields.length - 1 - index, 0) : 0;

        return acc + score + bonus;
    }, 0);
}

function searchByScore<T = Record<string, unknown> | string>(
    values: T[],
    query: string,
    fields?: (keyof T)[],
    skipSort?: boolean,
): T[] {
    if (!query) return values;

    // Pre-allocate array to avoid resizing
    const scoredValues = new Array(values.length);
    let validCount = 0;

    // Single pass to compute scores and filter
    for (let i = 0; i < values.length; i++) {
        let score = 0;
        const valueItem = values[i];

        if (typeof valueItem === 'string') {
            score = getScore(valueItem, query, []);
        } else {
            score = getObjectScore(valueItem, query, fields);
        }

        if (score >= MIN_SCORE_THRESHOLD) {
            scoredValues[validCount++] = {
                value: valueItem,
                score,
            };
        }
    }

    // Only sort the valid items
    scoredValues.length = validCount;
    if (!skipSort) {
        scoredValues.sort((a, b) => b.score - a.score);
    }

    // Reuse array to avoid allocation
    for (let i = 0; i < validCount; i++) {
        scoredValues[i] = scoredValues[i].value;
    }
    scoredValues.length = validCount;

    return scoredValues;
}

export default searchByScore;

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;

    describe('searchByScore', () => {
        describe('getScore', () => {
            it('returns 1 for exact matches', () => {
                expect(getScore('test', 'test', [])).toBe(1);
            });
            it('penalizes non-complete matches', () => {
                expect(getScore('testing', 'test', [])).toBe(PENALTY_NOT_COMPLETE);
            });
            it('scores word jumps higher than character jumps', () => {
                expect(getScore('hello world', 'hw', [])).toBeGreaterThan(getScore('hardware', 'hw', []));
            });
            it('includes aliases in scoring', () => {
                expect(getScore('main', 'alt', ['alt'])).toBeGreaterThan(0);
            });

            it('penalizes distance from start of string', () => {
                expect(getScore('hello world', 'world', [])).toBeGreaterThan(
                    getScore('hello beautiful world', 'hw', []),
                );
            });
        });

        describe('searchByScore (array of strings)', () => {
            it('handles empty array input', () => {
                expect(searchByScore([], 'test')).toEqual([]);
            });
            it('returns array for empty query', () => {
                expect(searchByScore(['test'], '')).toEqual(['test']);
            });
            it('returns matching items sorted by score', () => {
                const items = ['test case', 'test', 'testing'];
                expect(searchByScore(items, 'test')).toEqual(['test', 'test case', 'testing']);
            });
            it('returns fuzzy matches', () => {
                const items = ['Label List', 'Label', 'Modal'];
                expect(searchByScore(items, 'last')).toEqual(['Label List']);
            });
            it('handles case insensitive matches', () => {
                const items = ['Test', 'testing', 'TEST'];
                expect(searchByScore(items, 'test')).toEqual(['Test', 'TEST', 'testing']);
            });
            it('prioritizes matches at word boundaries', () => {
                const items = ['background-color', 'color-background', 'some-color'];
                expect(searchByScore(items, 'color')).toEqual(['color-background', 'background-color', 'some-color']);
            });
            it('handles special characters in search', () => {
                const items = ['user.name', 'user_name', 'userName'];
                expect(searchByScore(items, 'un')).toEqual(['user.name', 'user_name', 'userName']);
            });
            it('handles beginning of words', () => {
                const items = ['John Doe', 'Jane Doe', 'John Smith'];
                expect(searchByScore(items, 'jd')).toEqual(['John Doe', 'Jane Doe']);
            });
            it('prioritizes shorter matches over longer ones', () => {
                const items = ['button', 'big-button', 'button-group'];
                expect(searchByScore(items, 'button')).toEqual(['button', 'button-group', 'big-button']);
            });
            it('handles matches with transpositions', () => {
                const items = ['receive', 'recieve', 'receipt'];
                expect(searchByScore(items, 'recieve')).toEqual(['recieve', 'receive']);
            });
            it('handles matches with multiple word combinations', () => {
                const items = ['new york', 'new yrok', 'new yourk'];
                expect(searchByScore(items, 'new york')).toEqual(['new york', 'new yourk', 'new yrok']);
            });
        });

        describe('searchByScore (array of objects)', () => {
            it('handles empty array input', () => {
                expect(searchByScore([], 'test')).toEqual([]);
            });

            it('returns matching items', () => {
                const items = [{ name: 'Alex' }, { name: 'John' }, { name: 'Jane' }];
                expect(searchByScore(items, 'Alex', ['name'])).toEqual([{ name: 'Alex' }]);
            });

            it('respects fields order', () => {
                const items = [
                    { name: 'Pier Gasly', team: 'Alpine' },
                    { name: 'Franco Colapinto', team: 'Alpine' },
                    { name: 'Fernando Alonso', team: 'Aston Martin' },
                ];

                expect(searchByScore(items, 'Al', ['name', 'team'])).toEqual([
                    { name: 'Fernando Alonso', team: 'Aston Martin' },
                    { name: 'Franco Colapinto', team: 'Alpine' },
                    { name: 'Pier Gasly', team: 'Alpine' },
                ]);
            });

            it('filters out non-matching items', () => {
                const items = [
                    { name: 'Max Verstappen', team: 'Red Bull' },
                    { name: 'Sergio Perez', team: 'Red Bull' },
                    { name: 'Lewis Hamilton', team: 'Mercedes' },
                ];
                expect(searchByScore(items, 'ver', ['name'])).toEqual([{ name: 'Max Verstappen', team: 'Red Bull' }]);
            });

            it('filters by multiple fields but returns only matching items', () => {
                const items = [
                    { name: 'Lando Norris', team: 'McLaren' },
                    { name: 'Oscar Piastri', team: 'McLaren' },
                    { name: 'Lance Stroll', team: 'Aston Martin' },
                ];
                expect(searchByScore(items, 'mc', ['name', 'team'])).toEqual([
                    { name: 'Lando Norris', team: 'McLaren' },
                    { name: 'Oscar Piastri', team: 'McLaren' },
                ]);
            });

            it('searches by keywords', () => {
                const items = [
                    { name: 'Andrea Antonelli', keywords: ['Kimi'] },
                    { name: 'Kimi Räikkönen', keywords: ['Iceman'] },
                    { name: 'Carlos Sainz' },
                ];

                expect(searchByScore(items, 'Kimi', ['name'])).toEqual([
                    { name: 'Kimi Räikkönen', keywords: ['Iceman'] },
                    { name: 'Andrea Antonelli', keywords: ['Kimi'] },
                ]);
            });

            it('handles diacritics', () => {
                const items = [{ name: 'Cédric Carrasso' }, { name: 'Jérémy Olié' }, { name: 'João Lopes' }];
                expect(searchByScore(items, 'Cedric', ['name'])).toEqual([{ name: 'Cédric Carrasso' }]);
            });

            it('accepts non-string values', () => {
                const items = [
                    { name: { first: 'Andrea', last: 'Stella' }, team: 'McLaren' },
                    { name: 'Andrea Antonelli', team: 'Mercedes', keywords: ['Kimi'] },
                    { name: 'Charles Leclerc', team: 'Ferrari' },
                ];
                expect(searchByScore(items, 'Andrea', ['name'])).toEqual([
                    { name: 'Andrea Antonelli', keywords: ['Kimi'], team: 'Mercedes' },
                ]);
            });

            it('Customer case', () => {
                const items = [{ name: 'John O`Bryan' }, { name: 'Ryan Johnson' }, { name: 'John Doe' }];

                expect(searchByScore(items, 'Ryan', ['name'])).toEqual([
                    { name: 'Ryan Johnson' },
                    { name: 'John O`Bryan' },
                ]);
            });

            it('skips items with score below threshold', () => {
                const items = [
                    { name: 'Max Verstappen' },
                    { name: 'Sergio Perez' },
                    { name: 'Lewis Hamilton' },
                    { name: 'Lando Norris' },
                    { name: 'Fernando Alonso' },
                ];

                // Should not include Fernando Alonso if MIN_SCORE_THRESHOLD is 0.1 and applied
                expect(searchByScore(Array.from(items.values()), 'La', ['name'])).toEqual([
                    { name: 'Lando Norris' },
                    { name: 'Lewis Hamilton' },
                ]);
            });
        });
    });
}

// #endregion
